<template>
    <dashboard class="import">
        <heading>
            <h1 slot="title">Import</h1>
        </heading>

        <row>
            <panel size="col-24">
                <h2 slot="title">Steps</h2>
                <app-steps :active="step">
                    <app-step title="Upload"/>
                    <app-step title="Match"/>
                    <app-step title="Import"/>
                </app-steps>
            </panel>
        </row>

        <row v-if="step == 0">
            <panel size="col-24">
                <h3 slot="title">Type</h3>
                <div class="step">
                    <app-alert>Please select what type of data import you are wanting to perform.</app-alert>
                    <div class="card-options">
                        <app-card :selected="type == 'courses'" @click="handleRadioClick('courses')">
                            <template slot="title">Courses</template>
                            Import a list of students full scholastic schedules.
                        </app-card>
                        <app-card :selected="type == 'events'" @click="handleRadioClick('events')">
                            <template slot="title">Events</template>
                            Import events and associated students.
                        </app-card>
                        <app-card :selected="type == 'locations'" @click="handleRadioClick('locations')">
                            <template slot="title">Locations</template>
                            Import a list of locations to be created.
                        </app-card>
                        <app-card :selected="type == 'students'" @click="handleRadioClick('students')">
                            <template slot="title">Students</template>
                            Import students and their teams.
                        </app-card>
                    </div>
                </div>
            </panel>
        </row>
        <row v-if="step == 0">
            <panel size="col-24">
                <h3 slot="title">Upload</h3>
                <div class="step">
                    <upload-drop
                        :body="{ type }"
                        url="/api/v2/import/upload"
                        field-name="students"
                        @success="handleUpload"/>
                </div>
            </panel>
        </row>
        <row v-if="step == 1 && (type == 'courses' || type == 'events')">
            <panel size="col-24">
                <h3 slot="title">Options</h3>
                <div class="step">
                    <app-alert>
                        Please select if you would like this to be additive or a merge.
                        <br>
                        <br>
                        <strong>Add / Drop</strong> will remove all students from their current classes and replace them with the ones in the import.
                        <br>
                        <strong>Only Add</strong> will leave all students in their current classes and add the contents of the import to their schedules.
                    </app-alert>
                    <br>
                    <app-radio-group v-model="style">
                        <app-radio :label="0" border>Add / Drop</app-radio>
                        <app-radio :label="1" border>Only Add</app-radio>
                    </app-radio-group>
                </div>
            </panel>
        </row>
        <row v-if="step == 1" id="import-step-match">
            <panel size="col-24">
                <h3 slot="title">Match</h3>
                <app-button slot="buttons" type="danger" @click="clearHeaders">Clear Headers</app-button>
                <div class="step">
                    <app-alert v-if="matchError" type="error">{{ matchError }}</app-alert>
                    <app-alert
                        v-else>We've tried to best match the column headers of your spreadsheet to the fields we need. Please double check all of them below.</app-alert>
                    <div class="match-blocks">
                        <div class="match-block match-block-header">
                            <div class="match-block-column">Our Fields</div>
                            <div class="match-block-column quarter">Header</div>
                            <div class="match-block-column quarter">First Row Value</div>
                        </div>
                        <match-row
                            v-for="key in keys"
                            :key="key.value"
                            :ref="key.key"
                            v-model="map"
                            :options="headers"
                            :first-row="firstRow"
                            :field="key.key"
                            :label="key.value"
                            :last-map="lastMap[key.key]"
                            :required="key.required"
                            :has-last-map="hasLastMap"/>
                    </div>
                    <app-button type="primary" size="large" @click="handleMatch">Next</app-button>
                </div>
            </panel>
        </row>
        <row v-if="step >= 2">
            <panel size="col-24">
                <h3 v-if="step == 3" slot="title">Processing Import...</h3>
                <h3 v-else-if="step == 4" slot="title">Complete</h3>
                <h3 v-else slot="title">Waiting to Begin...</h3>
                <div class="step">
                    <app-alert
                        v-if="step == 2">Your file has been queued to import. Depending on the size of the file it could be a few minutes before the data is imported. Leave this page open to see the progress of the import.</app-alert>
                    <div v-if="step == 3">
                        <progress-bar :percentage="importObj.progress"/>
                    </div>
                    <div v-if="step > 3" class="totals">
                        <app-alert v-if="type == 'courses'" type="success">
                            Students: {{ importObj.student_count }}
                            <br>
                            Classes: {{ importObj.class_count }}
                            <br>
                            Locations: {{ importObj.location_count }}
                            <br>
                        </app-alert>
                        <app-alert v-else-if="type == 'events'" type="success">
                            Students: {{ importObj.student_count }}
                            <br>
                            Events: {{ importObj.event_count }}
                            <br>
                            Locations: {{ importObj.location_count }}
                            <br>
                        </app-alert>
                        <app-alert v-else-if="type == 'locations'" type="success">
                            Locations: {{ importObj.location_count }}
                            <br>
                        </app-alert>
                        <app-alert v-else type="success">
                            Students: {{ importObj.student_count }}
                            <br>
                        </app-alert>
                    </div>
                </div>
            </panel>
        </row>
    </dashboard>
</template>

<script>
import { first, get } from '@/utilities'

import axios from '@/network/axios'

import MatchRow from '../../ui/MatchRow'
import UploadDrop from '../../ui/UploadDrop'
export default {
    name: 'page-import',
    components: {
        MatchRow,
        UploadDrop
    },
    props: ['uuid'],
    data() {
        return {
            headers: [],
            firstRow: {},
            keys: [],
            map: {},
            matchError: null,
            type: 'courses',
            style: 0,
            importObj: null,
            interval: null,
            lastMap: {},
            hasLastMap: false
        }
    },
    computed: {
        step() {
            return get(this, 'importObj.status', 0)
        }
    },
    mounted() {
        if (this.uuid) {
            this.checkImport()
        }
    },
    beforeDestroy() {
        clearInterval(this.interval)
    },
    methods: {
        handleUpload(data) {
            this.headers = data.headers
            this.firstRow = data.firstRow
            this.keys = data.keys
            this.importObj = data.import

            if (data.lastMap) {
                this.hasLastMap = true
                this.lastMap = data.lastMap
            }

            this.$router.push({
                name: 'imports.detail',
                params: { uuid: data.import.uuid }
            })
        },
        postImport() {
            axios
                .post('/api/v2/import/process', {
                    map: this.map,
                    style: this.style,
                    uuid: this.uuid || this.importObj.uuid
                })
                .then(({ data }) => {
                    this.importObj = data
                    this.checkInterval()
                })
                .catch(({ response }) => {
                    this.matchError = first(get(response, 'data.messages'))
                    document
                        .getElementById('import-step-match')
                        .scrollIntoView()
                })
        },
        handleMatch() {
            if (this.style == 0) {
                this.$confirm({
                    title: 'Confirm',
                    message:
                        'Are you sure you want to remove all class associations between students and override the data with this import?'
                }).then(() => {
                    this.postImport()
                })
            }
            else {
                this.postImport()
            }
        },
        checkImport() {
            if (this.uuid == 'New') return
            axios
                .get('/api/v2/import/check', {
                    params: { uuid: this.uuid || this.importObj.uuid }
                })
                .then(({ data }) => {
                    this.importObj = data
                    this.type = this.importObj.type
                    //If page is reloaded while matching is not finished, send user back to upload view
                    if (this.importObj.status == 1) {
                        this.importObj.status = 0
                    }
                    this.checkInterval()
                })
        },
        checkInterval() {
            if (this.step >= 2 && !this.interval) {
                this.startInterval()
            }
            if (this.step == 4) {
                clearInterval(this.interval)
            }
        },
        startInterval() {
            this.interval = setInterval(this.checkImport, 2500)
        },
        clearHeaders() {
            this.$confirm({
                title: 'Confirm',
                message: 'Are you sure you want to clear all the headers?'
            })
                .then(() => {
                    for (var matchRow in this.$refs) {
                        //need index 0 because of issue with using v-ref in v-for
                        this.$refs[matchRow][0].handleClear()
                    }
                })
        },
        handleRadioClick(type) {
            this.type = type
            if (type == 'locations') {
                this.style = 1
            }
            else {
                this.style = 0
            }
        }
    }
}
</script>

<style lang="stylus">
@import '../../../stylus/variables'

.import
    .el-steps
        margin-top: 15px
        margin-bottom: -10px

    .step
        padding-top: 15px

    .card-options
        display: grid
        grid-template-columns: 1fr 1fr 1fr 1fr
        grid-column-gap: 20px
        margin-top: 20px

.match-blocks
    display: table
    width: 100%
    margin-bottom: 20px

    .match-block
        display: table-row

        &.match-block-header
            font-weight: bold

        .match-block-column
            padding: 5px 0px
            width: 40%
            display: table-cell

            &.quarter
                width: 30%
</style>

